import {
  ConversationControllerService,
  ConversationEscalationResponse,
  ConversationResponse
} from "@9amhealth/openapi";
import { parseStructuredContent } from "@9amhealth/shared";
import { Blac } from "blac-next";
import { globalEvents } from "src/constants/globalEvents";
import WebSocketBloc, {
  WebsocketMessageType,
  type ObserverCallback
} from "src/state/WebSocketBloc/WebSocketBloc";
import { DbConversationService } from "src/lib/db/DbConversationService";
import { invalidateOwnership } from "src/lib/db/DbService";
import type { CachedMessage } from "src/lib/db/dbTypes";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import { fileState } from "src/state/state";
import type {
  ChatPrompt,
  LeapChatAttachment,
  LeapChatFileRef,
  LeapChatMessage
} from "./types";
import { isEmptyNonLocalMessage } from "./utils";
import { featureFlags } from "src/lib/featureFlags";

type ConversationChangeCallback = (
  conversations: ConversationResponse[]
) => void;
type NewMessageCallback = (
  conversationId: string,
  message: LeapChatMessage
) => void;

interface ReceivedMessagePayload {
  id: string;
  messageOwnerId: string;
  senderId: string;
  senderMetadata?: {
    displayName?: string;
    displayRole?: string;
    role?: string;
  };
  contentType?: string;
  contentValue?: string;
  metadata?: {
    filesIdList?: string[];
    "message:original-source"?: string;
  };
  messageReceiveTimestamp?: string;
}

export class ConversationService {
  private static instance: ConversationService | null = null;

  static getInstance(): ConversationService {
    if (!this.instance) {
      this.instance = new ConversationService();
    }
    return this.instance;
  }

  static resetInstance(): void {
    this.instance = null;
  }

  get isEnabled(): boolean {
    return featureFlags.isConversationsEnabled;
  }

  private conversations: ConversationResponse[] = [];
  private conversationChangeListeners = new Set<ConversationChangeCallback>();
  private newMessageListeners = new Set<NewMessageCallback>();
  private pendingFirstMessage: {
    text: string;
    fileIds?: string[];
    files?: LeapChatFileRef[];
  } | null = null;
  private pendingReceivedMessages = new Map<string, ReceivedMessagePayload>();
  private activeConversationId: string | null = null;

  static readonly MAX_CACHED_MESSAGES = 100;
  private static readonly SYNC_FRESH_MS = 30_000;
  private messageSyncTimestamps = new Map<string, number>();
  private writeQueues = new Map<string, Promise<void>>();

  private enqueueWrite(
    conversationId: string,
    fn: () => Promise<void>
  ): Promise<void> {
    const prev = this.writeQueues.get(conversationId) ?? Promise.resolve();
    const next = prev.catch(() => {}).then(fn);
    this.writeQueues.set(conversationId, next);
    void next
      .catch(() => {})
      .finally(() => {
        if (this.writeQueues.get(conversationId) === next) {
          this.writeQueues.delete(conversationId);
        }
      });
    return next;
  }

  private constructor() {
    window.addEventListener(globalEvents.USER_CLEAR, this.handleUserClear);
    window.addEventListener(globalEvents.USER_CHANGED, this.handleUserChanged);
    this.registerWebSocket();
  }

  // -- Events --

  onConversationsChanged(cb: ConversationChangeCallback): () => void {
    this.conversationChangeListeners.add(cb);
    return () => this.conversationChangeListeners.delete(cb);
  }

  onNewMessage(cb: NewMessageCallback): () => void {
    this.newMessageListeners.add(cb);
    return () => this.newMessageListeners.delete(cb);
  }

  private notifyConversationsChanged(): void {
    for (const cb of this.conversationChangeListeners) {
      cb(this.conversations);
    }
  }

  private notifyNewMessage(
    conversationId: string,
    message: LeapChatMessage
  ): void {
    for (const cb of this.newMessageListeners) {
      cb(conversationId, message);
    }
  }

  // -- WebSocket --

  setActiveConversation(conversationId: string | null): void {
    this.activeConversationId = conversationId;
  }

  private registerWebSocket(): void {
    try {
      const ws = Blac.getBloc(WebSocketBloc);
      ws.addObserver(
        WebsocketMessageType.receivedMessage,
        this.handleReceivedMessage as ObserverCallback
      );
      ws.addObserver(
        WebsocketMessageType.conversationMessageAdded,
        this.handleConversationMessageAdded as ObserverCallback
      );
    } catch {
      // WebSocketBloc may not be available yet
    }
  }

  private handleReceivedMessage = (data?: {
    payload?: { payload?: ReceivedMessagePayload };
  }): void => {
    const msg = data?.payload?.payload;
    if (!msg?.id) {
      return;
    }
    if (msg.senderId && msg.senderId === msg.messageOwnerId) {
      return;
    }
    this.pendingReceivedMessages.set(msg.id, msg);
  };

  private handleConversationMessageAdded = (data?: {
    payload?: { conversationId?: string; messageId?: string };
  }): void => {
    const conversationId = data?.payload?.conversationId;
    const messageId = data?.payload?.messageId;

    if (!conversationId || !messageId) {
      return;
    }

    const isActive = this.activeConversationId === conversationId;

    if (!isActive) {
      this.updateConversation(conversationId, (c) => ({
        ...c,
        unreadMessageCount: (c.unreadMessageCount ?? 0) + 1
      }));
    }

    const rawMsg = this.pendingReceivedMessages.get(messageId);
    this.pendingReceivedMessages.delete(messageId);

    const message = rawMsg
      ? ConversationService.toLeapChatMessage(rawMsg)
      : null;

    if (message && !isEmptyNonLocalMessage(message)) {
      void this.cacheMessages(conversationId, [message]);
      this.notifyNewMessage(conversationId, message);
    }
  };

  static toLeapChatMessage(raw: ReceivedMessagePayload): LeapChatMessage {
    const role = raw.senderId === raw.messageOwnerId ? "user" : "assistant";
    const { text, toolCalls } = parseStructuredContent(
      raw.contentType,
      raw.contentValue
    );
    const filesIdList = raw.metadata?.filesIdList;
    const isGeneratedResponse =
      raw.metadata?.["message:original-source"] === "9am-ai";
    const files: LeapChatFileRef[] | undefined = filesIdList?.length
      ? filesIdList.map((fileId) => ({
          fileId,
          name: null,
          type: null,
          size: null
        }))
      : undefined;
    const smd = raw.senderMetadata;
    const senderName = smd?.displayName?.trim() || undefined;
    const jobTitleTranslation = smd?.role
      ? translate("job_title", { context: smd.role.toLowerCase() })
      : "";
    const senderDescription =
      smd?.displayRole || jobTitleTranslation || undefined;

    return {
      id: raw.id,
      role,
      text,
      toolCalls: toolCalls.length > 0 ? toolCalls : undefined,
      files,
      createdAt: raw.messageReceiveTimestamp
        ? Date.parse(raw.messageReceiveTimestamp)
        : Date.now(),
      senderId: role === "assistant" ? raw.senderId : undefined,
      senderName,
      senderDescription,
      isGeneratedResponse: isGeneratedResponse || undefined
    };
  }

  // -- API: Conversations --

  async listConversations(): Promise<ConversationResponse[]> {
    if (!this.isEnabled) {
      return this.conversations;
    }

    if (this.conversations.length === 0) {
      const cached = await DbConversationService.getConversations();
      if (cached.length > 0) {
        this.conversations = ConversationService.sortConversations(cached);
        this.notifyConversationsChanged();
      }
    }

    try {
      const res = await ConversationControllerService.listConversations(0, 50);
      const remote = res.data.content;
      this.conversations = ConversationService.sortConversations(remote);
      this.notifyConversationsChanged();

      const remoteIds = new Set(remote.map((c) => c.id));
      void DbConversationService.putConversations(remote).then(() =>
        DbConversationService.deleteConversationsNotIn(remoteIds)
      );

      return this.conversations;
    } catch (e) {
      reportErrorSentry(e);
      return this.conversations;
    }
  }

  async createConversation(): Promise<string | null> {
    if (!this.isEnabled) {
      return null;
    }

    try {
      const res = await ConversationControllerService.createConversation({});
      const { conversationId } = res.data;
      const now = new Date().toISOString();
      this.addConversation({
        id: conversationId,
        status: ConversationResponse.status.ACTIVE,
        escalations: [],
        unreadMessageCount: 0,
        createdAt: now,
        updatedAt: now
      });
      return conversationId;
    } catch (e) {
      reportErrorSentry(e);
      return null;
    }
  }

  async createEscalation(
    conversationId: string
  ): Promise<{ escalationId: string }> {
    if (!this.isEnabled) {
      throw new Error("Conversations are disabled");
    }

    const res =
      await ConversationControllerService.createEscalation(conversationId);
    return res.data;
  }

  async markRead(conversationId: string, messageId: string): Promise<void> {
    if (!this.isEnabled) {
      return;
    }

    try {
      await ConversationControllerService.setLastReadMessage(conversationId, {
        messageId
      });
      this.conversations = this.conversations.map((c) =>
        c.id === conversationId ? { ...c, unreadMessageCount: 0 } : c
      );
      this.notifyConversationsChanged();
      await this.listConversations();
    } catch (e) {
      reportErrorSentry(e);
    }
  }

  // -- API: Messages --

  async fetchMessages(conversationId: string): Promise<LeapChatMessage[]> {
    if (!this.isEnabled) {
      return [];
    }

    const res = await ConversationControllerService.listConversationMessages(
      conversationId,
      0,
      1000,
      "addedAt,asc"
    );
    return res.data.content
      .map((m): LeapChatMessage => {
        const metadata = (
          m.message as {
            metadata?: {
              filesIdList?: string[];
              "message:original-source"?: string;
            };
          }
        ).metadata;
        return ConversationService.toLeapChatMessage({
          id: m.message.id,
          senderId: m.message.senderId,
          messageOwnerId: m.message.messageOwnerId,
          contentType: m.message.contentType,
          contentValue: m.message.contentValue,
          senderMetadata: m.message.senderMetadata,
          metadata,
          messageReceiveTimestamp: m.addedAt
        });
      })
      .sort((a, b) => a.createdAt - b.createdAt);
  }

  // -- Cache --

  async getCachedMessages(conversationId: string): Promise<LeapChatMessage[]> {
    return DbConversationService.getMessages(conversationId);
  }

  async cacheMessages(
    conversationId: string,
    messages: LeapChatMessage[]
  ): Promise<void> {
    return this.enqueueWrite(conversationId, async () => {
      await DbConversationService.putMessages(
        conversationId,
        ConversationService.toCachedMessages(conversationId, messages)
      );
    });
  }

  async persistMessages(
    conversationId: string,
    messages: LeapChatMessage[]
  ): Promise<void> {
    return this.enqueueWrite(conversationId, async () => {
      await DbConversationService.putMessages(
        conversationId,
        ConversationService.toCachedMessages(conversationId, messages)
      );
      const currentIds = new Set(messages.map((m) => m.id));
      await DbConversationService.deleteMessagesNotIn(
        conversationId,
        currentIds
      );
    });
  }

  isMessageSyncFresh(conversationId: string): boolean {
    const lastSync = this.messageSyncTimestamps.get(conversationId) ?? 0;
    return Date.now() - lastSync < ConversationService.SYNC_FRESH_MS;
  }

  async syncMessagesCache(
    conversationId: string,
    remoteMessages: LeapChatMessage[]
  ): Promise<void> {
    this.messageSyncTimestamps.set(conversationId, Date.now());
    const filtered = remoteMessages.filter((m) => !isEmptyNonLocalMessage(m));
    return this.enqueueWrite(conversationId, async () => {
      const remoteIds = new Set(filtered.map((m) => m.id));
      await DbConversationService.putMessages(
        conversationId,
        ConversationService.toCachedMessages(conversationId, filtered)
      );

      const existing = await DbConversationService.getMessages(conversationId);
      const failedIds = existing.filter((m) => m.error).map((m) => m.id);
      const keepIds = new Set([...remoteIds, ...failedIds]);
      await DbConversationService.deleteMessagesNotIn(conversationId, keepIds);

      await DbConversationService.trimMessages(
        conversationId,
        ConversationService.MAX_CACHED_MESSAGES
      );
    });
  }

  async getCachedPrompts(conversationId: string): Promise<ChatPrompt[]> {
    return DbConversationService.getPrompts(conversationId);
  }

  async putPrompt(prompt: ChatPrompt): Promise<void> {
    await DbConversationService.putPrompt(prompt);
  }

  async resolvePromptsByType(
    conversationId: string,
    eventType: string,
    value: string
  ): Promise<void> {
    await DbConversationService.resolvePromptsByType(
      conversationId,
      eventType,
      value
    );
  }

  async deleteMessage(
    conversationId: string,
    messageId: string
  ): Promise<void> {
    return this.enqueueWrite(conversationId, async () => {
      await DbConversationService.deleteMessage(messageId);
    });
  }

  // -- Attachments --

  createAttachment(file: File): LeapChatAttachment {
    const key = crypto.randomUUID();
    const previewUrl = file.type.startsWith("image/")
      ? URL.createObjectURL(file)
      : null;
    return {
      key,
      fileId: null,
      name: file.name,
      type: file.type,
      size: file.size,
      previewUrl,
      status: "uploading"
    };
  }

  async uploadAttachmentFile(file: File): Promise<string | null> {
    const result = await fileState.uploadFile({ file });
    return result?.id ?? null;
  }

  revokePreviewUrls(attachments: LeapChatAttachment[]): void {
    for (const a of attachments) {
      if (a.previewUrl) {
        URL.revokeObjectURL(a.previewUrl);
      }
    }
  }

  extractAttachmentPayload(attachments: LeapChatAttachment[]): {
    fileIds: string[] | undefined;
    files: LeapChatFileRef[] | undefined;
  } {
    const doneAttachments = attachments.filter(
      (a): a is LeapChatAttachment & { fileId: string } =>
        a.status === "done" && a.fileId !== null
    );
    const fileIds = doneAttachments.map((a) => a.fileId);
    const files: LeapChatFileRef[] = doneAttachments.map((a) => ({
      fileId: a.fileId,
      name: a.name,
      type: a.type,
      size: a.size
    }));
    return {
      fileIds: fileIds.length > 0 ? fileIds : undefined,
      files: files.length > 0 ? files : undefined
    };
  }

  // -- Pending first message (new conversation flow) --

  setPendingFirstMessage(
    text: string,
    fileIds?: string[],
    files?: LeapChatFileRef[]
  ): void {
    this.pendingFirstMessage = { text, fileIds, files };
  }

  consumePendingFirstMessage(): {
    text: string;
    fileIds?: string[];
    files?: LeapChatFileRef[];
  } | null {
    const msg = this.pendingFirstMessage;
    this.pendingFirstMessage = null;
    return msg;
  }

  // -- Metadata --

  getConversation(id: string): ConversationResponse | null {
    return this.conversations.find((c) => c.id === id) ?? null;
  }

  getConversations(): ConversationResponse[] {
    return this.conversations;
  }

  updateConversation(
    id: string,
    updater: (c: ConversationResponse) => ConversationResponse
  ): void {
    this.conversations = this.conversations.map((c) =>
      c.id === id ? updater(c) : c
    );
    this.notifyConversationsChanged();
  }

  addConversation(conv: ConversationResponse): void {
    this.conversations = ConversationService.sortConversations([
      conv,
      ...this.conversations
    ]);
    this.notifyConversationsChanged();
  }

  static isEscalated(conv: ConversationResponse): boolean {
    return conv.escalations.some(
      (e) => e.status === ConversationEscalationResponse.status.OPEN
    );
  }

  isConversationEscalated(conversationId: string): boolean {
    const conv = this.getConversation(conversationId);
    return conv ? ConversationService.isEscalated(conv) : false;
  }

  // -- Cleanup --

  private resetInMemoryState(): void {
    this.conversations = [];
    this.pendingFirstMessage = null;
    this.pendingReceivedMessages.clear();
    this.activeConversationId = null;
    this.messageSyncTimestamps.clear();
    this.writeQueues.clear();
  }

  private handleUserClear = (): void => {
    this.resetInMemoryState();
    invalidateOwnership();
    void DbConversationService.clearAll();
    this.notifyConversationsChanged();
  };

  private handleUserChanged = (): void => {
    this.resetInMemoryState();
    invalidateOwnership();
    this.notifyConversationsChanged();
  };

  // -- Helpers --

  private static toCachedMessages(
    conversationId: string,
    messages: LeapChatMessage[]
  ): CachedMessage[] {
    return messages.map((m) => ({ ...m, conversationId }));
  }

  private static sortConversations(
    list: ConversationResponse[]
  ): ConversationResponse[] {
    return [...list].sort(
      (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)
    );
  }
}
